import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02Icon, ColorsIcon, LayoutGridIcon, PaintBrush02Icon, SparklesIcon } from "@hugeicons/core-free-icons";
import { Template } from "@/types";

interface Props {
  templates: Template[];
  isLoading: boolean;
  selectedTemplateId: string;
  onSelect: (templateId: string) => void;
}

const getFallbackIcon = (title: string, index: number) => {
  const t = title.toLowerCase();
  if (t.includes("minimal") || t.includes("paint") || index === 0) {
    return <HugeiconsIcon icon={PaintBrush02Icon} size={44} strokeWidth={1.5} color="#a0a0a0" />;
  }
  if (t.includes("elegan") || t.includes("spark") || index === 1) {
    return <HugeiconsIcon icon={SparklesIcon} size={44} strokeWidth={1.5} color="#a0a0a0" />;
  }
  return <HugeiconsIcon icon={ColorsIcon} size={44} strokeWidth={1.5} color="#a0a0a0" />;
};

const DesignSection = ({ templates, isLoading, selectedTemplateId, onSelect }: Props) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <HugeiconsIcon icon={LayoutGridIcon} size={32} color={theme.palette.primary.main} />
            <Typography variant="h3">Plantillas de Diseño</Typography>
          </Stack>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Cargando plantillas...
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (templates.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Stack spacing={3}>
        {/* Section Title with Icon */}
        <Stack direction="row" spacing={2} alignItems="center">
          <HugeiconsIcon icon={LayoutGridIcon} size={32} color={theme.palette.primary.main} />
          <Typography variant="h3">Plantillas de Diseño</Typography>
        </Stack>

        {/* Templates horizontal list */}
        <Stack
          direction="row"
          spacing={2.5}
          sx={{
            overflowX: "auto",
            pb: 1,
            pt: 0.5,
            "::-webkit-scrollbar": {
              height: "6px",
            },
            "::-webkit-scrollbar-track": {
              background: "rgba(0,0,0,0.02)",
            },
            "::-webkit-scrollbar-thumb": {
              background: "rgba(0,0,0,0.12)",
              borderRadius: "4px",
            },
          }}
        >
          {templates.map((template, index) => {
            const isSelected = selectedTemplateId === template.id;

            return (
              <Stack key={template.id} spacing={1} sx={{ minWidth: 150, alignItems: "center" }}>
                <Box
                  onClick={() => onSelect(template.id)}
                  sx={{
                    position: "relative",
                    width: 150,
                    height: 200,
                    borderRadius: "16px",
                    border: isSelected ? "2.5px solid" : "1px solid",
                    borderColor: isSelected ? "primary.main" : "rgba(0,0,0,0.12)",
                    bgcolor: "rgba(0,0,0,0.03)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    overflow: "hidden",
                    boxShadow: isSelected ? "0px 4px 20px rgba(0, 88, 188, 0.12)" : "none",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0px 6px 20px rgba(0,0,0,0.08)",
                      borderColor: isSelected ? "primary.main" : "rgba(0,0,0,0.24)",
                      "& img": {
                        transform: "scale(1.05)",
                      },
                    },
                  }}
                >
                  {/* Selected checkmark badge */}
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0px 2px 8px rgba(0, 88, 188, 0.3)",
                        zIndex: 2,
                      }}
                    >
                      <HugeiconsIcon icon={CheckmarkCircle02Icon} size={14} strokeWidth={3} />
                    </Box>
                  )}

                  {/* Template Image or Fallback Illustration */}
                  {template.image ? (
                    <Box
                      component="img"
                      src={template.image}
                      alt={template.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                      }}
                    />
                  ) : (
                    getFallbackIcon(template.title, index)
                  )}
                </Box>

                {/* Template Title */}
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: isSelected ? "primary.main" : "text.secondary",
                    textAlign: "center",
                    transition: "color 0.2s ease",
                  }}
                >
                  {template.title}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default DesignSection;
